import React, { useState } from "react";
import { Menu, X } from "lucide-react";
import axios from "axios";

import DashboardSidebar from "../../components/dashboard components/DashboardSidebar";
import DashboardHeader from "../../components/dashboard components/DashboardHeader";

import ProjectHomeTabForm from "../../components/dashboard components/forms/ProjectHomeTabForm";
import ProjectGalleryTabForm from "../../components/dashboard components/forms/ProjectGalleryTabForm";
import ProjectPlanForm from "../../components/dashboard components/forms/ProjectPlanForm";
import ProjectLocationForm from "../../components/dashboard components/forms/ProjectLocationForm";
import ProjectAmenitiesForm from "../../components/dashboard components/forms/ProjectAmenitiesForm";
import ProjectApprovalComplianceForm from "../../components/dashboard components/forms/ProjectApprovalComplianceForm";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CreateProjects = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("home");
  const [tabMenuOpen, setTabMenuOpen] = useState(false);

  const tabs = [
    { id: "home", label: "Home" },
    { id: "gallery", label: "Gallery" },
    { id: "plan", label: "Project Plan" },
    { id: "location", label: "Location Map" },
    { id: "amenities", label: "Amenities" },
    { id: "approval", label: "Approvals & Compliances" },
  ];

  const activeTabLabel =
    tabs.find((t) => t.id === activeTab)?.label || "Select Section";

  const [homeData, setHomeData] = useState({
    project_name: "",
    project_location: "",
    project_description: "",
    project_rera: "",
    project_status: "",
    project_extent: "",
    project_badge_location: "",
    project_type: "",
    project_price: "",
    project_complete_date: "",
    approval: "",
    hmda: "",
    features: "",
    project_image: "",
    brochure: "",
    gallery_description: "",
    gallery_images: [],
    project_amenities: [],
    project_plan_images: [],
    approvals_compliances: [],
  });

  const deleteDraft = async (draftId) => {
    if (!draftId) return;

    const token = localStorage.getItem("token");
    try {
      const res = await axios.delete(
        `${API_BASE_URL}/admin/delete-draft-project/${draftId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("Draft deleted:", res.data);
      localStorage.removeItem("draftId");
    } catch (error) {
      console.error("Error deleting draft:", error);
    }
  };

  const handleSaveHome = async (overrides = {}) => {
    try {
      const token = localStorage.getItem("token");
      const merged = { ...homeData, ...overrides };

      const ensureArray = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) return val;
        if (typeof val === "string") {
          try {
            const parsed = JSON.parse(val);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        }
        return [];
      };

      const jsonData = {
        project_name: merged.project_name || "",
        project_location: merged.project_location || "",
        project_description: merged.project_description || "",
        project_rera: merged.project_rera || "",
        project_status: merged.project_status || "",
        project_extent: merged.project_extent || "",
        project_badge_location: merged.project_badge_location || "",
        project_type: merged.project_type || "",
        project_price: merged.project_price || "",
        project_complete_date: merged.project_complete_date || "",
        approval: merged.approval || "",
        hmda: merged.hmda || "",
        features: merged.features || "",
        project_image: merged.project_image || "",
        brochure: merged.brochure || "",
        gallery_description: merged.gallery_description || "",

        gallery_images: ensureArray(merged.gallery_images),
        project_plan_images: ensureArray(merged.project_plan_images),
        project_amenities: ensureArray(merged.project_amenities),
        approvals_compliances: ensureArray(merged.approvals_compliances),
      };

      console.log("📦 Full payload:", JSON.stringify(jsonData, null, 2));

      const response = await axios.post(
        `${API_BASE_URL}/admin/add-project`,
        jsonData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Saved successfully:", response.data);

      setHomeData({
        project_name: "",
        project_location: "",
        project_description: "",
        project_rera: "",
        project_status: "",
        project_extent: "",
        project_badge_location: "",
        project_type: "",
        project_price: "",
        project_complete_date: "",
        approval: "",
        hmda: "",
        features: "",
        project_image: "",
        brochure: "",
        gallery_description: "",
        gallery_images: [],
        project_amenities: [],
        project_plan_images: [],
        approvals_compliances: [],
      });

      deleteDraft(localStorage.getItem("draftId"));

      alert("Project saved successfully!");
      window.location.reload();
    } catch (error) {
      console.error("Full error:", error);
      console.error("Response:", error.response?.data);
      console.error("Status:", error.response?.status);

      const errorMsg = error.response?.data?.message || error.message;
      alert(`Failed to save: ${errorMsg}`);
    }
  };

  const TabButton = ({ id, label }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`px-5 py-2 text-sm md:text-base font-semibold border-b-4 transition-all duration-300 ${
        activeTab === id
          ? "border-blue-600 text-blue-700 bg-blue-50"
          : "border-transparent text-gray-600 hover:text-blue-600 hover:bg-gray-50"
      }`}
    >
      {label}
    </button>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case "home":
        return (
          <ProjectHomeTabForm
            homeData={homeData}
            setHomeData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
          />
        );
      case "gallery":
        return (
          <ProjectGalleryTabForm
            galleryData={homeData}
            setGalleryData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
          />
        );
      case "plan":
        return (
          <ProjectPlanForm
            lightboxData={homeData}
            setLightboxData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
          />
        );
      case "location":
        return (
          <ProjectLocationForm
            placeholderData={homeData}
            setPlaceholderData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
          />
        );
      case "amenities":
        return (
          <ProjectAmenitiesForm
            amenitiesData={homeData}
            setAmenitiesData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
          />
        );
      case "approval":
        return (
          <ProjectApprovalComplianceForm
            approvalData={homeData}
            setApprovalData={setHomeData}
            onSave={handleSaveHome}
            setActiveTab={setActiveTab}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      <div className="hidden lg:block">
        <DashboardSidebar />
      </div>

      <div
        className={`fixed inset-y-0 left-0 bg-white w-72 shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:hidden`}
      >
        <DashboardSidebar />
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-blue-600"
          onClick={() => setSidebarOpen(false)}
        >
          <X size={28} />
        </button>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <main className="flex-1 flex flex-col w-full">
        <div className="sticky top-0 bg-white z-30 shadow-sm flex items-center justify-between px-4 sm:px-8 py-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-700 hover:text-blue-600"
          >
            <Menu size={28} />
          </button>

          <div className="flex-1 flex justify-center">
            <DashboardHeader
              title="Create Project - Editor"
              backTo="/dashboard"
            />
          </div>
        </div>

        <div className="bg-white border-b border-gray-200 shadow-sm mt-4">
          <div className="px-4 py-2 flex items-center justify-between lg:hidden">
            <span className="font-semibold text-gray-800 text-sm">
              {activeTabLabel}
            </span>
            <button
              onClick={() => setTabMenuOpen((prev) => !prev)}
              className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <span>Sections</span>
              <Menu size={18} />
            </button>
          </div>

          {tabMenuOpen && (
            <div className="px-4 pb-3 space-y-1 lg:hidden">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setTabMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium ${
                    activeTab === tab.id
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          <div className="hidden lg:flex justify-center gap-2 sm:gap-4 px-2 sm:px-4 py-2 flex-wrap">
            {tabs.map((tab) => (
              <TabButton key={tab.id} id={tab.id} label={tab.label} />
            ))}
          </div>
        </div>

        <div className="p-4 sm:p-8 md:p-10 max-w-6xl mx-auto w-full bg-white rounded-xl shadow-md mb-6 mt-4">
          {renderActiveTab()}
        </div>
      </main>
    </div>
  );
};

export default CreateProjects;
